import React from "react"
import Supercluster from "supercluster"
import { Marker, useMap } from "react-map-gl"
import { BikesCluster, calcClusterStatePercentage, SUPERCLUSTER_OPTIONS } from "../lib/helpers/clusters"
import { MapBikeEntity } from "../lib/hooks/queries/useGetMapBikesQuery"
import { Bbox, getBikeFeatures, getBounds } from "../lib/helpers/map"
import { BikeMarker } from "./BikeMarker"
import { calcAssetState } from "../lib/helpers/calcMarket"
import { BikesClusterMarker } from "./BikesClusterMarker"

interface BikesMapMapboxSuperclusterProps {
  bikes: MapBikeEntity[]
  activeBike: MapBikeEntity | null
  zoomOnClick?: boolean
  zoomOnClickPadding?: number
}

const DEFAULT_PADDING = 20

const BikesMapMapboxSupercluster: React.FC<BikesMapMapboxSuperclusterProps> = ({
  bikes,
  activeBike,
  zoomOnClick,
  zoomOnClickPadding,
}) => {
  const { bikesMapbox } = useMap()
  const [superCluster, setSuperCluster] = React.useState<Supercluster>()
  const [currentClusters, setCurrentClusters] = React.useState<BikesCluster[]>()
  const currentClustersLength = currentClusters?.length

  // define update method
  const updateClusters = React.useCallback(
    (superC: Supercluster, forceSetState?: boolean) => {
      if (!bikesMapbox || !superC) return
      const zoom = bikesMapbox.getZoom()
      const canvas = bikesMapbox.getCanvas()
      const w = canvas.width
      const h = canvas.height
      const upRight = bikesMapbox.unproject([w, 0]).toArray()
      const downLeft = bikesMapbox.unproject([0, h]).toArray()
      const bbox = [
        [downLeft[0], downLeft[1]],
        [upRight[0], upRight[1]],
      ] as Bbox
      const newPoints = superC.getClusters(
        [...bbox[0], ...bbox[1]], // [westLng, southLat, eastLng, northLat]
        Math.round(zoom),
      ) as BikesCluster[]

      if (newPoints.length !== currentClustersLength || forceSetState) {
        setCurrentClusters(newPoints)
      }
    },
    [bikesMapbox, currentClustersLength],
  )

  // on change method
  const onMapChange = React.useCallback(() => {
    if (!superCluster) return
    updateClusters(superCluster)
  }, [superCluster, updateClusters])

  // update supercluster when map moves
  React.useEffect(() => {
    if (!bikesMapbox) return
    bikesMapbox.on("move", onMapChange)
    bikesMapbox.on("zoom", onMapChange)
    return () => {
      bikesMapbox?.off("move", onMapChange)
      bikesMapbox?.off("zoom", onMapChange)
    }
  }, [bikesMapbox, onMapChange])

  // init supercluster
  React.useEffect(() => {
    const points = bikes?.map(getBikeFeatures) ?? []
    const superC = new Supercluster({
      ...SUPERCLUSTER_OPTIONS,
      extent: 512,
    })
    superC.load(points)
    setSuperCluster(superC)
    updateClusters(superC, true)
  }, [bikes, updateClusters])

  const fitBoundsOnClick = React.useCallback(
    (clusterId?: number) => {
      if (!superCluster || !clusterId || !bikesMapbox) return
      const leaves = superCluster
        ?.getLeaves(clusterId, Infinity)
        .map((leaf: any) => leaf.geometry.coordinates)
      const bounds = getBounds(leaves)
      const padding = zoomOnClickPadding ?? DEFAULT_PADDING
      bikesMapbox.fitBounds(bounds, { padding })
    },
    [superCluster, bikesMapbox, zoomOnClickPadding],
  )

  return (
    <>
      {currentClusters?.map((feature) => {
        const key = feature.properties.cluster
          ? `cluster_${feature.id}`
          : `asset_${feature.properties.bicycle?.uuid}`

        if (feature.properties.cluster) {
          const { uuids, stateCounts } = calcClusterStatePercentage({
            superClusterInstance: superCluster,
            currentCluster: feature,
          })

          return (
            <Marker
              key={key}
              anchor="center"
              longitude={feature.geometry.coordinates[0]}
              latitude={feature.geometry.coordinates[1]}
              onClick={zoomOnClick ? () => fitBoundsOnClick(feature.properties.cluster_id) : undefined}
            >
              <BikesClusterMarker
                count={feature.properties?.point_count ?? 0}
                uuids={uuids}
                stateCounts={stateCounts}
              />
            </Marker>
          )
        } else if (feature.properties.bicycle) {
          const bike = feature.properties.bicycle
          return (
            <Marker
              key={key}
              anchor="center"
              longitude={bike.longitude}
              latitude={bike.latitude}
              data-state={calcAssetState(bike)}
              data-uiud={bike.uuid}
            >
              <BikeMarker bike={bike} isActive={activeBike?.uuid === bike?.uuid} />
            </Marker>
          )
        }

        return null
      })}
    </>
  )
}

export default BikesMapMapboxSupercluster
